import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./styles/index.css";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter } from "react-router";
import { version } from "../package.json";
import { ErrorBoundaryProvider } from "./components/errorHandling/ErrorBoundaryProvider.tsx";
import { Toaster } from "./components/ui/toaster.tsx";
import { TooltipProvider } from "./components/ui/tooltip.tsx";
import { AuthProvider } from "./context/AuthContext.tsx";
import { MosaicProvider } from "./context/mosaic/MosaicContext.tsx";
import { TitleMapProvider } from "./context/mosaic/TitleMosaicProvider.tsx";
import { TabsProvider } from "./context/tabs/TabsContext.tsx";
import { settings } from "./lib/utils/settings/settings.ts";

Sentry.init({
	dsn: import.meta.env.VITE_APP_ENV ? settings.sentryDsn : "",
	environment: import.meta.env.VITE_APP_ENV,
	release: version,
	allowUrls: [/https?:\/\/([a-z0-9-]+\.)*epsmomentum\.com/],
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],
	// Tracing
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: [/^https:\/\/.*api\.epsmomentum\.com\//],
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	ignoreErrors: [/Network Error/i, /net::ERR_/i],
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

ReactDOM.createRoot(document.getElementById("root")!).render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<AuthProvider>
					<TabsProvider>
						<TitleMapProvider>
							<MosaicProvider>
								<TooltipProvider>
									<ErrorBoundaryProvider>
										<App />
									</ErrorBoundaryProvider>
								</TooltipProvider>
								<Toaster scope="tabs" />
							</MosaicProvider>
						</TitleMapProvider>
					</TabsProvider>
				</AuthProvider>
			</BrowserRouter>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
		</QueryClientProvider>
	</React.StrictMode>,
);
